import { Dialog } from "@base-ui/react/dialog"
import PropTypes from "prop-types"
import React, { useRef } from "react"

const sizes = {
  small: "lg:w-96",
  mediumFixed: "lg:w-150",
  large: "lg:w-3/5",
  extraLarge: "lg:w-4/5",
  default: "lg:w-5/12",
  mediumResponsive: "lg:w-3/5 2xl:w-5/12",
}

const minSizes = {
  default: "",
  medium: "min-w-[800px]",
}

const maxSizes = {
  default: "",
  medium: "max-w-[600px]",
}

const ModalDesktop = ({
  onClose,
  children,
  isOpen,
  afterLeave,
  size = "default",
  minSize = "default",
  maxSize = "default",
  initialFocusRef,
  stacked,
}) => {
  const closeIconRef = useRef(null)
  const focusRef = initialFocusRef || closeIconRef

  const sizeClass = sizes[size]
  const minSizeClass = minSizes[minSize]
  const maxSizeClass = maxSizes[maxSize]
  const zIndexClass = stacked ? "z-modal-stacked" : "z-modal"

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      onOpenChangeComplete={(open) => {
        if (!open) afterLeave?.()
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop
          className={`${zIndexClass} fixed inset-0 bg-black/25 transition-opacity duration-300 ease-in-out data-[closed]:opacity-0 data-[open]:opacity-100 data-[starting-style]:opacity-0`}
        />
        <div
          data-design-system="Modal"
          className={`${zIndexClass} fixed bottom-4 left-4 right-4 top-1 flex max-h-screen items-center justify-center sm:items-start md:top-20`}
        >
          <Dialog.Popup
            initialFocus={focusRef}
            className={`w-full ${sizeClass} ${minSizeClass} ${maxSizeClass} flex max-h-full flex-col rounded bg-white p-4 text-left align-middle shadow-xl transition-all duration-300 ease-in-out data-[closed]:scale-95 data-[open]:scale-100 data-[starting-style]:scale-95 data-[closed]:opacity-0 data-[open]:opacity-100 data-[starting-style]:opacity-0`}
          >
            <button
              ref={closeIconRef}
              type="button"
              className="btn-sm z-1 absolute right-2 top-2 flex h-8 w-8 items-center justify-center bg-transparent text-2xl font-semibold text-gray-700 hover:text-gray-500 focus:outline-none focus:ring"
              onClick={onClose}
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
            {children}
          </Dialog.Popup>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

ModalDesktop.propTypes = {
  children: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  afterLeave: PropTypes.func,
  size: PropTypes.oneOf([
    "small",
    "mediumFixed",
    "large",
    "extraLarge",
    "default",
    "mediumResponsive",
  ]),
  minSize: PropTypes.oneOf(["default", "medium"]),
  maxSize: PropTypes.oneOf(["default", "medium"]),
  initialFocusRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  stacked: PropTypes.bool,
}

export default ModalDesktop
